
import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faBars } from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/AllServices.css";
import AddNewService from "./AddNewService";
import { useTranslation } from "react-i18next";
import { getAllServices, deleteService, deletePlan } from "./servicesApi";
import Swal from "sweetalert2";
import { de } from "date-fns/locale";
import StatusBadge from "../common/StatusBadge";


const TableRow = ({
  service,
  plan,
  index,
  totalRows,
  onEditClick,
  onEditPlanClick, // new callback
  onDeleteClick,
  onDeletePlanClick, // new callback for plan deletion
  isOpen,
  onToggle,
}) => {
  const isNearBottom = index >= totalRows - 2;
  const dropdownDirectionClass = isNearBottom
    ? "dropdown-menu-up"
    : "dropdown-menu-end";
  const dropdownPositioningStyle = isNearBottom
    ? { position: "absolute", right: 0, bottom: "100%" }
    : { position: "absolute", right: 0 };
  const { t } = useTranslation();

  // Extract backend fields
  const serviceName = service.serviceInfo?.SERVICE_NAME || "-";
  const category = service.serviceInfo?.SERVICE_CATEGORY || "-";
  const releaseDate = service.serviceReleaseDate || "-";
  const status = service.serviceStatus === "1" ? "Active" : "Inactive";

  // Use the specific plan passed as prop
  const planName = plan ? plan.PLAN_NAME : "-";
  const price = plan ? plan.PLAN_TOTAL_COST : "-";
  const planStatus = plan ? (plan.PLAN_STATUS === "1" ? "Active" : "Inactive") : "-";

  return (
    <tr>
      <td data-label={t("serviceName")}>{serviceName}</td>
      <td data-label={t("category")}>{category}</td>
      <td data-label={t("activationDate")}>{releaseDate}</td>
      <td data-label={t("servicePlan")}>{planName}</td>
      <td data-label={t("price_label")}>{price}</td>
      <td data-label={t("status")}>{planStatus}</td>
      <td data-label={t("edit")} className="text-end">
        <div className="position-relative dropdown-wrapper">
          <button className="btn btn-sm btn-light" onClick={onToggle}>
            <FontAwesomeIcon icon={faEllipsisV} />
          </button>
          {isOpen && (
            <div
              className={`dropdown-menu shadow-sm show ${dropdownDirectionClass}`}
              style={dropdownPositioningStyle}
            >
              <button
                className="dropdown-item"
                onClick={() => {
                  onEditClick({
                    ...service,
                    serviceInfo: {
                      ...service.serviceInfo,
                      SERVICE_NAME: serviceName !== "-" ? serviceName : "",
                      SERVICE_CATEGORY: category !== "-" ? category : "",
                    },
                    planInfo: plan
                      ? {
                        PLAN_NAME: plan.PLAN_NAME || "",
                        PLAN_TOTAL_COST: plan.PLAN_TOTAL_COST || "",
                        PLAN_TOTAL_CALL_MINUTES:
                          plan.PLAN_TOTAL_CALL_MINUTES || 0,
                        PLAN_TOTAL_MESSAGES:
                          plan.PLAN_TOTAL_MESSAGES || 0,
                      }
                      : null,
                  });
                }}
              >
                {t("editServices")}
              </button>

              {/* New: Edit Plan */}
              {plan && (
                <button
                  className="dropdown-item"
                  onClick={() =>                  
                      onEditPlanClick({
                      serviceId: service.serviceInfo?.SERVICE_ID,
                      planId: plan.PLAN_ID,
                      planName: plan.PLAN_NAME,
                      planTotalCallMinutes: plan.PLAN_TOTAL_CALL_MINUTES,
                      planTotalMessages: plan.PLAN_TOTAL_MESSAGES,
                      planProductPrice: plan.PLAN_TOTAL_COST,
                      planStatus: plan.PLAN_STATUS,
                      planPsfCallMinutes: plan.PLAN_PSF_CALL_MINUTES,
                      planPsfMessages: plan.PLAN_PSF_MESSAGES,
                      planSmrCallMinutes: plan.PLAN_SMR_CALL_MINUTES,
                      planSmrMessages: plan.PLAN_SMR_MESSAGES,
                    })
                  }
                >
                  {t("editPlan")}
                </button>
              )}

              {/* Delete Plan */}
              {plan && (
                <button
                  className="dropdown-item"
                  onClick={() => onDeletePlanClick({
                    serviceId: service.serviceInfo?.SERVICE_ID,
                    planId: plan.PLAN_ID,
                    planName: plan.PLAN_NAME
                  })}
                >
                  {t("deletePlan")}
                </button>
              )}

              {/* Delete Service */}
              <button
                className="dropdown-item"
                onClick={() => onDeleteClick(service.serviceInfo?.SERVICE_ID)}
              >
                {t("deleteService")}
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

// -------------------- ServiceCardRow (mobile) --------------------
const ServiceCardRow = ({
  service,
  plan,
  index,
  totalRows,
  onEditClick,
  onEditPlanClick,
  onDeleteClick,
  onDeletePlanClick,
  isOpen,
  onToggle,
}) => {
  const isNearBottom = index >= totalRows - 2;
  const dropdownDirectionClass = isNearBottom ? "dropdown-menu-up" : "dropdown-menu-end";
  const dropdownPositioningStyle = isNearBottom
    ? { position: "absolute", right: 0, bottom: "100%" }
    : { position: "absolute", right: 0 };
  const { t } = useTranslation();

  const serviceName = service.serviceInfo?.SERVICE_NAME || "-";
  const category = service.serviceInfo?.SERVICE_CATEGORY || "-";
  const releaseDate = service.serviceReleaseDate || "-";
  const status = service.serviceStatus === "1" ? "Active" : "Inactive";
  const planName = plan ? plan.PLAN_NAME : "-";
  const price = plan ? plan.PLAN_TOTAL_COST : "-";
  const planStatus = plan ? (plan.PLAN_STATUS === "1" ? "Active" : "Inactive") : "-";

  return (
    <div className="service-card">
      <div className="service-card-header">
        <div className="service-card-title">{serviceName}</div>
        <div className="position-relative dropdown-wrapper">
          <button className="btn btn-sm btn-light" onClick={onToggle}>
            <FontAwesomeIcon icon={faEllipsisV} />
          </button>
          {isOpen && (
            <div
              className={`dropdown-menu shadow-sm show ${dropdownDirectionClass}`}
              style={dropdownPositioningStyle}
            >
              <button
                className="dropdown-item"
                onClick={() => {
                  onEditClick({
                    ...service,
                    serviceInfo: {
                      ...service.serviceInfo,
                      SERVICE_NAME: serviceName !== "-" ? serviceName : "",
                      SERVICE_CATEGORY: category !== "-" ? category : "",
                    },
                  });
                }}
              >
                {t("editServices")}
              </button>

              {plan && (
                <button
                  className="dropdown-item"
                  onClick={() =>
                    onEditPlanClick({
                      serviceId: service.serviceInfo?.SERVICE_ID,
                      planId: plan.PLAN_ID,
                      planName: plan.PLAN_NAME,
                      planTotalCallMinutes: plan.PLAN_TOTAL_CALL_MINUTES,
                      planTotalMessages: plan.PLAN_TOTAL_MESSAGES,
                      planProductPrice: plan.PLAN_TOTAL_COST,
                      planStatus: plan.PLAN_STATUS,
                    })
                  }
                >
                  {t("editPlan")}
                </button>
              )}

              {plan && (
                <button
                  className="dropdown-item"
                  onClick={() => onDeletePlanClick({
                    serviceId: service.serviceInfo?.SERVICE_ID,
                    planId: plan.PLAN_ID,
                    planName: plan.PLAN_NAME
                  })}
                >
                  {t("deletePlan")}
                </button>
              )}

              <button
                className="dropdown-item"
                onClick={() => onDeleteClick(service.serviceInfo?.SERVICE_ID)}
              >
                {t("deleteService")}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="kv-grid">
        <div className="kv">
          <div className="label">{t("serviceName")}</div>
          <div className="value">{serviceName}</div>
        </div>
        <div className="kv">
          <div className="label">{t("price_label")}</div>
          <div className="value">{price}</div>
        </div>

        <div className="kv">
          <div className="label">{t("activationDate")}</div>
          <div className="value">{releaseDate}</div>
        </div>
        <div className="kv">
          <div className="label">{t("servicePlan")}</div>
          <div className="value">{planName}</div>
        </div>

        <div className="kv">
          <div className="label">{t("category")}</div>
          <div className="value">{category}</div>
        </div>
        <div className="kv">
          <div className="label">{t("status")}</div>
          <div className="value"><StatusBadge status={planStatus} /> </div>
        </div>
      </div>
    </div>
  );
};

// -------------------- ServiceTable --------------------
const ServiceTable = ({ services, onEditClick, onEditPlanClick, onDeleteClick, onDeletePlanClick }) => {
  const { t } = useTranslation();
  const [openRowIndex, setOpenRowIndex] = useState(null);

  // Create flattened array of service-plan combinations
  const servicePlanRows = services.flatMap((service) => {
    const plans = service.planInfo || [];
    if (plans.length === 0) {
      // If no plans, show service with null plan
      return [{ service, plan: null }];
    }
    // Create a row for each plan
    return plans.map((plan) => ({ service, plan }));
  });

  const totalRows = servicePlanRows.length;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".dropdown-wrapper")) {
        setOpenRowIndex(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleToggle = (index) => {
    setOpenRowIndex((prev) => (prev === index ? null : index));
  };

  return (
    <div>
      <div className="services-table-wrapper responsive-table-wrapper desktop-only">
        <table className="services-table responsive-table">
        <thead>
          <tr>
            <th>{t("serviceName")}</th>
            <th>{t("category")}</th>
            <th>{t("activationDate")}</th>
            <th>{t("plans")}</th>
            <th>{t("price_label")}</th>
            <th>{t("planStatus")}</th>
            <th>{t("edit")}</th>
          </tr>
        </thead>

        <tbody>
          {servicePlanRows.map((row, index) => (
            <TableRow
              key={`${row.service.serviceInfo?.SERVICE_ID}-${row.plan?.PLAN_ID || 'no-plan'}-${index}`}
              service={row.service}
              plan={row.plan}
              index={index}
              totalRows={totalRows}
              onEditClick={onEditClick}
              onEditPlanClick={onEditPlanClick}
              onDeleteClick={onDeleteClick}
              onDeletePlanClick={onDeletePlanClick}
              isOpen={openRowIndex === index}
              onToggle={() => handleToggle(index)}
            />
          ))}
        </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="service-cards mobile-only">
        {servicePlanRows.map((row, index) => (
          <ServiceCardRow
            key={`card-${row.service.serviceInfo?.SERVICE_ID}-${row.plan?.PLAN_ID || 'no-plan'}-${index}`}
            service={row.service}
            plan={row.plan}
            index={index}
            totalRows={totalRows}
            onEditClick={onEditClick}
            onEditPlanClick={onEditPlanClick}
            onDeleteClick={onDeleteClick}
            onDeletePlanClick={onDeletePlanClick}
            isOpen={openRowIndex === index}
            onToggle={() => handleToggle(index)}
          />
        ))}
      </div>
    </div>
  );
};

// -------------------- Pagination --------------------
const Pagination = ({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const pageNumbers = [];

  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
  } else {
    if (currentPage <= 3) {
      pageNumbers.push(1, 2, 3, "...", totalPages);
    } else if (currentPage >= totalPages - 2) {
      pageNumbers.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
    } else {
      pageNumbers.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages
      );
    }
  }

  return (
    <nav aria-label="Page navigation">
      <ul
        className="pagination"
        style={{ justifyContent: "flex-end", marginRight: "50px" }}
      >
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button
            className="page-link"
            onClick={() => onPageChange(currentPage - 1)}
          >
            &lt;
          </button>
        </li>
        {pageNumbers.map((number, index) => (
          <li
            key={index}
            className={`page-item ${currentPage === number ? "active" : ""} ${number === "..." ? "disabled" : ""
              }`}
          >
            {number === "..." ? (
              <span className="page-link">...</span>
            ) : (
              <button
                className="page-link"
                onClick={() => onPageChange(number)}
              >
                {number}
              </button>
            )}
          </li>
        ))}
        <li
          className={`page-item ${currentPage === totalPages ? "disabled" : ""
            }`}
        >
          <button
            className="page-link"
            onClick={() => onPageChange(currentPage + 1)}
          >
            &gt;
          </button>
        </li>
      </ul>
    </nav>
  );
};

// -------------------- AllServices --------------------
const AllServices = () => {
  const [servicesData, setServicesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [statusFilter, setStatusFilter] = useState("1"); // "1"=active, "0"=inactive
  const [nameFilter, setNameFilter] = useState("All");
  const [planFilter, setPlanFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [isAddPlanDropdownOpen, setIsAddPlanDropdownOpen] = useState(false);
  const [selectedServiceForPlan, setSelectedServiceForPlan] = useState(null);
  const [showAddPlan, setShowAddPlan] = useState(false);

 // ✅ Get user role from session or local storage (MSIL flow may set in either)
 const userRole = sessionStorage.getItem("role") || localStorage.getItem("role");


  // Modal states
  const [showAddService, setShowAddService] = useState(false);
  const [serviceToEdit, setServiceToEdit] = useState(null);

  const { t } = useTranslation();

  const loadServices = async (status = "all") => {
    try {
      const data = await getAllServices(status);
      setServicesData(data);
      setFilteredData(data);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };


  const isSuperAdmin = userRole === "SUPER_ADMIN";

  const showPermissionDenied = () => {
    Swal.fire({
      title: "Permission Denied",
      text: "You do not have permission to perform this action.",
      icon: "error",
      timer: 2000,
      showConfirmButton: false,
    });
  };

const handleEditPlanClick = (planData) => {
  if (!isSuperAdmin) return showPermissionDenied();

  // We can reuse AddNewService modal in plan-only mode with edit mode
  const serviceForEdit = {
    serviceInfo: { SERVICE_ID: planData.serviceId },
    planInfo: [{
      PLAN_ID: planData.planId,
      PLAN_NAME: planData.planName,
      PLAN_TOTAL_CALL_MINUTES: planData.planTotalCallMinutes,
      PLAN_TOTAL_MESSAGES: planData.planTotalMessages,
      PLAN_TOTAL_COST: planData.planProductPrice,
      PLAN_STATUS: planData.planStatus,
      PLAN_PSF_CALL_MINUTES: planData.planPsfCallMinutes,
      PLAN_PSF_MESSAGES: planData.planPsfMessages,
      PLAN_SMR_CALL_MINUTES: planData.planSmrCallMinutes,
      PLAN_SMR_MESSAGES: planData.planSmrMessages,
    }], // only the plan being edited with correct structure
  };

  console.log("Edit Plan - Setting selectedServiceForPlan:", serviceForEdit);
  console.log("Edit Plan - PLAN_ID exists:", !!serviceForEdit.planInfo?.[0]?.PLAN_ID);
  console.log("Edit Plan - PLAN_TOTAL_CALL_MINUTES:", serviceForEdit.planInfo?.[0]?.PLAN_TOTAL_CALL_MINUTES);

  setSelectedServiceForPlan(serviceForEdit);
  setShowAddPlan(true);
};


  const handleDeleteClick = async (serviceId) => {
    if (!isSuperAdmin) return showPermissionDenied();

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    if (result.isConfirmed) {
      try {
        const numericId = serviceId.includes('#') ? serviceId.split('#')[1] : serviceId;
        await deleteService(numericId);

        //show success alert with auto close after 1.5 seconds
        Swal.fire({
          title: "Deleted!",
          text: "Service has been deleted.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        //Reload services
        await loadServices();
      } catch (error) {
        console.error("Error deleting service:", error);


        //show error alert
        Swal.fire({
          title: "Error!",
          text: "Failed to delete service.Please try again.",
          icon: "error",
          timer: 2000,
          confirmButtonText: false,
        });
      }
    }
  }

  const handleDeletePlanClick = async ({ serviceId, planId, planName }) => {
    if (!isSuperAdmin) return showPermissionDenied();

    // Check if this is the last plan
    const service = servicesData.find(s => s.serviceInfo?.SERVICE_ID === serviceId);
    const planCount = service?.planInfo?.length || 0;

    if (planCount <= 1) {
      Swal.fire({
        title: "Cannot Delete Last Plan",
        text: "A service must have at least one plan. You cannot delete the only remaining plan.",
        icon: "error",
        confirmButtonText: "OK"
      });
      return;
    }

    const result = await Swal.fire({
      title: "Delete Plan?",
      html: `Are you sure you want to delete the plan "<strong>${planName}</strong>"?<br/>This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    if (result.isConfirmed) {
      try {
        await deletePlan(serviceId, planId);

        Swal.fire({
          title: "Deleted!",
          text: "Plan has been deleted.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        await loadServices();
      } catch (error) {
        console.error("Error deleting plan:", error);

        Swal.fire({
          title: "Error!",
          text: "Failed to delete plan. Please try again.",
          icon: "error",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    }
  }

  // Unique Service Names
  const uniqueServiceNames = useMemo(() => {
    const names = (servicesData ?? [])
      .map((s) => s?.serviceInfo?.SERVICE_NAME?.trim())
      .filter(Boolean);
    return ["All", ...Array.from(new Set(names))];
  }, [servicesData]);

  useEffect(() => {
    if (!uniqueServiceNames.includes(nameFilter)) {
      setNameFilter("All");
    }
  }, [uniqueServiceNames]);

  // Unique Plan Names
  const uniquePlanNames = useMemo(() => {
    const plans = (servicesData ?? [])
      .flatMap((s) => s?.planInfo?.map((p) => p?.PLAN_NAME?.trim()) || [])
      .filter(Boolean);
    return ["All", ...Array.from(new Set(plans))];
  }, [servicesData]);

  useEffect(() => {
    if (!uniquePlanNames.includes(planFilter)) {
      setPlanFilter("All");
    }
  }, [uniquePlanNames]);

  useEffect(() => {
    loadServices(statusFilter);
  }, [statusFilter]);

  // Unique categories
  const uniqueCategories = [
    "All",
    ...new Set(
      servicesData.map((service) => service.serviceInfo?.SERVICE_CATEGORY)
    ),
  ];



  const handleAddServiceClick = () => {
    if (!isSuperAdmin) return showPermissionDenied();
    setServiceToEdit(null);
    setShowAddService(true);
  };

  // show all services for Add Plan dropdown (both active and inactive)
  const allServicesForPlan = useMemo(() => {
    return servicesData;
  }, [servicesData]);



  const handleSelectServiceForPlan = (service) => {
    // When adding a new plan, pass service info ans status without plan data
    setSelectedServiceForPlan({
      serviceInfo: service.serviceInfo,
      serviceStatus: service.serviceStatus,
      // Don't include planInfo when adding a new plan
    });
    setIsAddPlanDropdownOpen(false);
    setShowAddPlan(true); // open plan creation modal

    console.log("Selected service to add plan for:", service.serviceInfo?.SERVICE_NAME);
    console.log("Service Status:", service.serviceStatus);
  };


  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".btn-primary") && !e.target.closest(".dropdown-menu")) {
        setIsAddPlanDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);


  const handleCloseAddService = async () => {
    setShowAddService(false);
    setServiceToEdit(null);
    await loadServices(statusFilter);
  };

  const handleEditClick = (service) => {
    if (!isSuperAdmin) return showPermissionDenied();
    setServiceToEdit(service);
    setShowAddService(true);
  };


  const handleCategorySelect = (category) => {
    setCategoryFilter(category);
    setCurrentPage(1);
    setIsCategoryDropdownOpen(false);
  };

  // Apply filters
  useEffect(() => {
    const filtered = servicesData.filter((service) => {
      const matchStatus = service.serviceStatus === statusFilter;
      const matchName =
        nameFilter === "All" ||
        service.serviceInfo?.SERVICE_NAME === nameFilter;
      const matchCategory =
        categoryFilter === "All" ||
        service.serviceInfo?.SERVICE_CATEGORY === categoryFilter;

      // For plan filter, check if any plan matches
      const matchPlan = planFilter === "All" ||
        (service.planInfo && service.planInfo.some(plan => plan.PLAN_NAME === planFilter));

      return matchStatus && matchName && matchPlan && matchCategory;
    });

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [statusFilter, nameFilter, planFilter, categoryFilter, servicesData]);

  // Pagination slicing
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentServices = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="container-fluid bg-light responsive-container">
      <div className="rounded responsive-container" style={{ backgroundColor: "#f4f4f4" }}>
        {showAddPlan ? (
          <AddNewService
            onClose={() => setShowAddPlan(false)}
            onServiceSaved={() => loadServices(statusFilter)}
            service={selectedServiceForPlan} // prefill with selected service
            startWithPlanTab={true} // optional flag to auto-open plan creation tab
            planOnlyMode={true}                // lock service fields + hide back button
            editPlanMode={selectedServiceForPlan?.planInfo?.[0]?.PLAN_ID ? true : false} // true if editing existing plan
          />
        ) : showAddService ? (
          <AddNewService
            onClose={handleCloseAddService}
            onServiceSaved={() => loadServices(statusFilter)}
            service={serviceToEdit}
          />
        ) : (
          <>
            <h1>{t("allServices")}</h1>

            {/* Filters */}
            <div
              className="d-flex flex-wrap align-items-center mb-3"
              style={{
                gap: "40px",
                padding: "15px 20px",
                backgroundColor: "#bcbcbc",
                marginTop: "20px",
                borderRadius: "8px",
              }}
            >
              {/* Status Filter */}
              <div
                className="filter-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "8px",
                }}
              >
                <label
                  htmlFor="status"
                  style={{
                    margin: 0,
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                  }}
                >
                  {t("status")}
                </label>
                <select
                  id="status"
                  className="form-select form-select-sm"
                  style={{
                    width: "150px",
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="1">{t("active")}</option>
                  <option value="0">{t("inactive")}</option>
                </select>
              </div>

              {/* Service Name Filter */}
              <div
                className="filter-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "8px",
                }}
              >
                <label
                  htmlFor="service-name"
                  style={{
                    margin: 0,
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                  }}
                >
                  {t("serviceName")}
                </label>
                <select
                  id="service-name"
                  className="form-select form-select-sm"
                  style={{
                    width: "150px",
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                >
                  {uniqueServiceNames.map((name) => (
                    <option key={name} value={name}>
                      {name === "All" ? t("all") : name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Plan Filter */}
              <div
                className="filter-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: "8px",
                }}
              >
                <label
                  htmlFor="service-plan"
                  style={{
                    margin: 0,
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                  }}
                >
                  {t("servicePlan")}
                </label>
                <select
                  id="service-plan"
                  className="form-select form-select-sm"
                  style={{
                    width: "150px",
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                >
                  {uniquePlanNames.map((plan) => (
                    <option key={plan} value={plan}>
                      {plan === "All" ? t("all") : plan}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Category Dropdown & Add Service */}
            <div
              className="d-flex justify-content-end"
              style={{ marginRight: "50px" }}
            >
              <div
                className="position-relative"
                onMouseEnter={() => setIsCategoryDropdownOpen(true)}
                onMouseLeave={() => setIsCategoryDropdownOpen(false)}
              >
                <button className="btn btn-primary me-2 btn-sm">
                  <FontAwesomeIcon icon={faBars} className="me-2" />{" "}
                  {t("categories")}
                </button>
                {isCategoryDropdownOpen && (
                  <div
                    className="dropdown-menu shadow-sm show"
                    style={{
                      position: "absolute",
                      right: 0,
                      zIndex: 1050,
                      maxHeight: "280px",
                      overflowY: "auto"
                    }}
                  >
                    {uniqueCategories.map((category) => (
                      <button
                        key={category}
                        className={`dropdown-item ${categoryFilter === category ? "active" : ""
                          }`}
                        onClick={() => handleCategorySelect(category)}
                      >
                        {category === "All" ? t("all") : category}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                className="btn btn-primary btn-sm me-2"
                onClick={handleAddServiceClick}
                disabled={!isSuperAdmin}

              >
                + {t("addService")}
              </button>

              {/* Add Plan Dropdown */}
              <div
                className="position-relative"
                onMouseEnter={() => isSuperAdmin && setIsAddPlanDropdownOpen(true)}
                onMouseLeave={() => setIsAddPlanDropdownOpen(false)}
              >
                <button
                  className="btn btn-primary btn-sm me-2"
                  disabled={!isSuperAdmin}
                >
                  + {t("addPlan")}
                </button>

                {isAddPlanDropdownOpen && isSuperAdmin && (
                  <div
                    className="dropdown-menu shadow-sm show"
                    style={{
                      position: "absolute",
                      right: 0,
                      zIndex: 1050,
                      maxHeight: "200px",
                      overflowY: "auto",
                      width: "250px",
                    }}
                  >
                    {allServicesForPlan.length === 0 ? (
                      <span className="dropdown-item text-muted">No Services Available</span>
                    ) : (
                      allServicesForPlan.map((service) => (
                        <button
                          key={service.serviceInfo?.SERVICE_ID}
                          className="dropdown-item"
                          onClick={() => handleSelectServiceForPlan(service)}
                        >
                          {service.serviceInfo?.SERVICE_NAME || "Unnamed Service"}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>


            </div>

            {/* Table */}
            <ServiceTable
              services={currentServices}
              onEditClick={handleEditClick}
              onEditPlanClick={handleEditPlanClick} // new callback
              onDeleteClick={handleDeleteClick}
              onDeletePlanClick={handleDeletePlanClick}
            />

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3 px-4">
              <div className="text-muted small">
                {t("showingData", {
                  start: indexOfFirstItem + 1,
                  end: Math.min(indexOfLastItem, filteredData.length),
                  total: filteredData.length,
                })}
              </div>
              <Pagination
                totalItems={filteredData.length}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AllServices;